import { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, MessageCircle, Send } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

export default function Footer() {
    const { settings } = useSettings();
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);

    const wa = (settings.whatsapp || "").replace(/[^0-9]/g, "");

    const subscribe = async (e) => {
        e.preventDefault();
        if (!email) return;
        setLoading(true);
        try {
            const { data } = await api.post("/newsletter", { email });
            toast.success(data.message || "Subscribed!");
            setEmail("");
        } catch (err) {
            toast.error(apiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <footer className="bg-ink text-cream mt-24">
            <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-16">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
                    <div className="md:col-span-4">
                        <div className="font-serif text-3xl tracking-[0.25em]">ELVOSH</div>
                        <p className="text-gold mt-2 text-sm tracking-[0.2em] uppercase">{settings.tagline || "Elevate Your Beauty"}</p>
                        <p className="text-cream/60 mt-5 text-sm max-w-xs leading-relaxed">
                            Premium hair styling tools, thoughtfully designed for effortless everyday beauty.
                        </p>
                        <div className="flex items-center gap-4 mt-6">
                            {settings.instagram_url && (
                                <a data-testid="footer-instagram" href={settings.instagram_url} target="_blank" rel="noopener noreferrer" className="hover:text-gold transition-colors"><Instagram className="h-5 w-5" strokeWidth={1.5} /></a>
                            )}
                            {settings.facebook_url && (
                                <a data-testid="footer-facebook" href={settings.facebook_url} target="_blank" rel="noopener noreferrer" className="hover:text-gold transition-colors"><Facebook className="h-5 w-5" strokeWidth={1.5} /></a>
                            )}
                            {wa && (
                                <a data-testid="footer-whatsapp" href={`https://wa.me/${wa}`} target="_blank" rel="noopener noreferrer" className="hover:text-gold transition-colors"><MessageCircle className="h-5 w-5" strokeWidth={1.5} /></a>
                            )}
                        </div>
                    </div>

                    <div className="md:col-span-2">
                        <h4 className="text-xs uppercase tracking-[0.2em] text-gold mb-4">Explore</h4>
                        <ul className="space-y-2.5 text-sm text-cream/70">
                            <li><Link to="/shop" className="hover:text-cream transition-colors">Shop</Link></li>
                            <li><Link to="/about" className="hover:text-cream transition-colors">About</Link></li>
                            <li><Link to="/contact" className="hover:text-cream transition-colors">Contact</Link></li>
                            <li><Link to="/faq" className="hover:text-cream transition-colors">FAQ</Link></li>
                            <li><Link to="/track" className="hover:text-cream transition-colors">Track Order</Link></li>
                        </ul>
                    </div>

                    <div className="md:col-span-3">
                        <h4 className="text-xs uppercase tracking-[0.2em] text-gold mb-4">Policies</h4>
                        <ul className="space-y-2.5 text-sm text-cream/70">
                            <li><Link to="/policy/warranty" className="hover:text-cream transition-colors">Warranty</Link></li>
                            <li><Link to="/policy/shipping" className="hover:text-cream transition-colors">Shipping Policy</Link></li>
                            <li><Link to="/policy/return" className="hover:text-cream transition-colors">Return & Refund Policy</Link></li>
                            <li><Link to="/policy/privacy" className="hover:text-cream transition-colors">Privacy Policy</Link></li>
                            <li><Link to="/policy/terms" className="hover:text-cream transition-colors">Terms & Conditions</Link></li>
                        </ul>
                    </div>

                    <div className="md:col-span-3">
                        <h4 className="text-xs uppercase tracking-[0.2em] text-gold mb-4">Newsletter</h4>
                        <p className="text-sm text-cream/70 mb-4">Be the first to know about new launches and offers.</p>
                        <form onSubmit={subscribe} className="flex items-center border-b border-cream/30 focus-within:border-gold transition-colors">
                            <input
                                data-testid="footer-newsletter-input"
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Your email"
                                className="w-full bg-transparent py-2 text-sm outline-none placeholder:text-cream/40"
                            />
                            <button data-testid="footer-newsletter-submit" disabled={loading} className="text-gold hover:text-cream transition-colors p-1">
                                <Send className="h-4 w-4" strokeWidth={1.5} />
                            </button>
                        </form>
                    </div>
                </div>

                <div className="border-t border-cream/10 mt-12 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-cream/50">
                    <p>© {new Date().getFullYear()} ELVOSH. All rights reserved.</p>
                    <div className="flex items-center gap-3">
                        {settings.instagram_url && (
                            <a data-testid="footer-bottom-instagram" href={settings.instagram_url} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="h-9 w-9 rounded-full border border-cream/20 flex items-center justify-center hover:border-gold hover:text-gold transition-colors"><Instagram className="h-4 w-4" strokeWidth={1.5} /></a>
                        )}
                        {settings.facebook_url && (
                            <a data-testid="footer-bottom-facebook" href={settings.facebook_url} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="h-9 w-9 rounded-full border border-cream/20 flex items-center justify-center hover:border-gold hover:text-gold transition-colors"><Facebook className="h-4 w-4" strokeWidth={1.5} /></a>
                        )}
                    </div>
                </div>
            </div>
        </footer>
    );
}
